/**
 * Song Requests - the channel page panel (R25 chat 2, R6).
 *
 * Rendered by `pages/ChannelDetail.tsx` below the chat, for a channel with
 * the module enabled. The place for the queue's bigger actions: skip, move a
 * request (to the front / up / down), cancel one, clear them all. The
 * dashboard widget is the read-only glance.
 *
 * `.btn` has no disabled style in the design system (index.css), so the
 * buttons here carry their own `disabled:` classes.
 *
 * One read - the module's `status` route, every 5s and straight after any
 * action. The track's progress is counted on between polls while it plays.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ChevronDown,
  ChevronUp,
  ChevronsUp,
  ListMusic,
  Loader2,
  Music,
  SkipForward,
  Star,
  Trash2,
  X,
} from 'lucide-react'
import clsx from 'clsx'
import { apiErrorMessage } from '@/api/client'
import { useToast } from '@/components/Toast'
import type { ChannelPanelProps } from '../types'
import {
  mmss,
  playerOfflineText,
  songRequestsApi,
  type MoveWhere,
  type QueueItem,
  type SongRequestStatus,
} from './api'

const POLL_MS = 5000

function songLine(title: string | null, artist: string | null): string {
  if (!title) return ''
  return artist ? `${title} — ${artist}` : title
}

export function SongRequestsQueuePanel({ channelId }: ChannelPanelProps) {
  const { success, error } = useToast()
  const [data, setData] = useState<SongRequestStatus | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [fetchedAt, setFetchedAt] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const alive = useRef(true)

  const refresh = useCallback(async () => {
    try {
      const status = await songRequestsApi.status(channelId)
      if (!alive.current) return
      setData(status)
      setFetchedAt(Date.now())
      setLoadError(null)
    } catch (err) {
      // Only a FIRST load failure is shown (the page has nothing else to
      // show); a failed poll keeps the last good view and tries again.
      if (alive.current) setLoadError(apiErrorMessage(err, 'Could not load the song-request queue'))
    }
  }, [channelId])

  useEffect(() => {
    alive.current = true
    refresh()
    const poll = setInterval(refresh, POLL_MS)
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => {
      alive.current = false
      clearInterval(poll)
      clearInterval(tick)
    }
  }, [refresh])

  const act = async (key: string, call: () => Promise<unknown>, ok: string | null, fail: string) => {
    setBusy(key)
    try {
      await call()
      if (ok) success(ok)
    } catch (err) {
      error(apiErrorMessage(err, fail))
    } finally {
      setBusy(null)
      refresh()
    }
  }

  const handleSkip = () =>
    act(
      'skip',
      async () => {
        const r = await songRequestsApi.skip(channelId)
        success(
          r.started
            ? `Skipped — up next: ${songLine(r.started.song.title, r.started.song.artist)}`
            : 'Skipped',
        )
      },
      null,
      'Could not skip',
    )

  const handleMove = (item: QueueItem, where: MoveWhere) =>
    act(`move-${item.id}`, () => songRequestsApi.move(channelId, item.id, where), null, 'Could not move that request')

  const handleCancel = (item: QueueItem) =>
    act(
      `cancel-${item.id}`,
      () => songRequestsApi.cancel(channelId, item.id),
      `Removed ${item.song.title}`,
      'Could not remove that request',
    )

  const handleClear = () => {
    if (!data || data.queue_length === 0) return
    if (!confirm(`Remove all ${data.queue_length} queued request${data.queue_length === 1 ? '' : 's'}? The song playing now carries on.`)) return
    act(
      'clear',
      async () => {
        const r = await songRequestsApi.clear(channelId)
        success(`Cleared ${r.cleared} request${r.cleared === 1 ? '' : 's'}`)
      },
      null,
      'Could not clear the queue',
    )
  }

  const player = data?.player
  const playing = player?.connected && player.title ? player : null
  let progress = playing?.progress_seconds ?? null
  if (progress != null && playing?.state === 'playing' && fetchedAt) {
    progress = Math.min(progress + (now - fetchedAt) / 1000, playing.duration_seconds ?? Infinity)
  }
  const pct =
    progress != null && playing?.duration_seconds
      ? Math.min(100, (progress / playing.duration_seconds) * 100)
      : null

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
          <ListMusic size={20} />
          Song requests
          {data && <span className="badge-neutral">{data.queue_length} queued</span>}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSkip}
            disabled={!player?.connected || busy !== null}
            className="btn-secondary btn-sm flex items-center gap-2 disabled:opacity-40 disabled:pointer-events-none"
            title={player?.connected ? 'Skip to the next request' : playerOfflineText(player?.reason ?? null)}
          >
            {busy === 'skip' ? <Loader2 className="animate-spin" size={14} /> : <SkipForward size={14} />}
            Skip
          </button>
          <button
            onClick={handleClear}
            disabled={!data || data.queue_length === 0 || busy !== null}
            className="btn-ghost btn-sm flex items-center gap-2 hover:text-state-danger disabled:opacity-40 disabled:pointer-events-none"
            title="Remove every queued request"
          >
            {busy === 'clear' ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
            Clear
          </button>
        </div>
      </div>

      {!data && loadError && <p className="text-sm text-state-danger">{loadError}</p>}
      {!data && !loadError && (
        <div className="flex justify-center py-6">
          <Loader2 className="animate-spin text-text-muted" size={24} />
        </div>
      )}

      {data && (
        <>
          {/* Now playing */}
          <div className="rounded-lg bg-surface-app p-3 mb-4">
            {!player?.connected ? (
              <p className="text-sm text-text-muted flex items-center gap-2">
                <Music size={16} />
                {playerOfflineText(player?.reason ?? null)}
              </p>
            ) : !playing ? (
              <p className="text-sm text-text-muted flex items-center gap-2">
                <Music size={16} />
                Nothing is playing
              </p>
            ) : (
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {songLine(playing.title, playing.artist)}
                    </p>
                    <p className="text-xs text-text-muted">
                      {playing.requested_by ? `Requested by @${playing.requested_by}` : "The streamer's music"}
                      {playing.ad_playing && ' · an ad is playing'}
                    </p>
                  </div>
                  <span
                    className={clsx(
                      playing.state === 'playing' ? 'badge-success' : 'badge-neutral',
                      'shrink-0',
                    )}
                  >
                    {playing.state === 'playing' ? 'Playing' : playing.state === 'paused' ? 'Paused' : playing.state}
                  </span>
                </div>
                {pct != null && (
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    <span className="font-mono">{mmss(progress)}</span>
                    <div className="flex-1 h-1 rounded-full bg-surface-overlay overflow-hidden">
                      <div className="h-full bg-synth-cyan" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="font-mono">{mmss(playing.duration_seconds)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* The queue */}
          {data.queue.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-4">
              The queue is empty — viewers add songs with !sr.
            </p>
          ) : (
            <ol className="space-y-1">
              {data.queue.map((item, i) => {
                const rowBusy = busy === `move-${item.id}` || busy === `cancel-${item.id}`
                return (
                  <li
                    key={item.id}
                    className="flex items-center gap-3 rounded bg-surface-base px-3 py-2"
                  >
                    <span className="w-6 text-right text-xs font-mono text-text-muted">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-text-primary truncate flex items-center gap-1">
                        {item.is_priority && (
                          <Star size={12} className="text-synth-magenta shrink-0" aria-label="Priority" />
                        )}
                        {songLine(item.song.title, item.song.artist)}
                      </p>
                      <p className="text-xs text-text-muted">
                        @{item.requester.username} · {mmss(item.song.duration_seconds)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {rowBusy && <Loader2 className="animate-spin text-text-muted" size={14} />}
                      <button
                        onClick={() => handleMove(item, 'front')}
                        disabled={i === 0 || busy !== null}
                        className="btn-ghost btn-sm px-1.5 disabled:opacity-30 disabled:pointer-events-none"
                        title="Move to the front"
                      >
                        <ChevronsUp size={16} />
                      </button>
                      <button
                        onClick={() => handleMove(item, 'up')}
                        disabled={i === 0 || busy !== null}
                        className="btn-ghost btn-sm px-1.5 disabled:opacity-30 disabled:pointer-events-none"
                        title="Move up"
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        onClick={() => handleMove(item, 'down')}
                        disabled={i === data.queue.length - 1 || busy !== null}
                        className="btn-ghost btn-sm px-1.5 disabled:opacity-30 disabled:pointer-events-none"
                        title="Move down"
                      >
                        <ChevronDown size={16} />
                      </button>
                      <button
                        onClick={() => handleCancel(item)}
                        disabled={busy !== null}
                        className="btn-ghost btn-sm px-1.5 disabled:opacity-30 disabled:pointer-events-none hover:text-state-danger"
                        title="Remove this request"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ol>
          )}
          {data.queue_length > data.queue.length && (
            <p className="help-text text-center">
              Showing the first {data.queue.length} of {data.queue_length}.
            </p>
          )}
          <p className="help-text mt-3">
            Priority requests (★) always play first; moving a request keeps it within its group.
          </p>
        </>
      )}
    </div>
  )
}

export default SongRequestsQueuePanel
